import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Coins, Receipt, ShieldCheck } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export default function Account() {
  const { user, balance } = useAuth();
  const [ledger, setLedger] = useState(null);

  useEffect(() => {
    api.get("/tokens/ledger").then(({ data }) => setLedger(data)).catch(() => setLedger([]));
  }, []);

  return (
    <div className="max-w-3xl space-y-8" data-testid="account-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Account</h1>
        <p className="mt-1 text-muted-foreground">Your profile, tokens and usage.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="text-sm text-muted-foreground">Signed in as</div>
          <div className="mt-1 font-display text-lg font-semibold">{user?.name}</div>
          <div className="text-sm text-muted-foreground">{user?.email}</div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-accent px-2.5 py-1 font-medium capitalize">{user?.role}</span>
            <span className="rounded-full bg-accent px-2.5 py-1 font-medium">{user?.country}</span>
            <span className="rounded-full bg-accent px-2.5 py-1 font-medium uppercase">{user?.data_region} region</span>
          </div>
        </div>
        <div className="rounded-2xl integrity-gradient p-6 text-white">
          <div className="flex items-center gap-2 text-sm opacity-85"><Coins className="h-4 w-4" /> Token balance</div>
          <div className="mt-1 font-display text-4xl font-extrabold" data-testid="account-balance">{balance}</div>
          <div className="mt-1 text-sm opacity-85">Free tokens expire {user?.free_expires_at ? new Date(user.free_expires_at).toLocaleDateString() : "—"}</div>
          <a href="/app/billing" data-testid="account-buy-tokens" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/25">
            <Coins className="h-4 w-4" /> Buy more tokens
          </a>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="mb-4 flex items-center gap-2 font-semibold"><Receipt className="h-4 w-4" /> Usage history</div>
        {ledger === null ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full rounded-lg" />)}</div>
        ) : ledger.length === 0 ? (
          <p className="text-sm text-muted-foreground">No activity yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {ledger.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2.5 text-sm">
                <div>
                  <div className="font-medium capitalize">{l.action.replace(/_/g, " ")}</div>
                  <div className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</div>
                </div>
                <span className={`font-mono font-semibold ${l.tokens >= 0 ? "text-[#059669]" : "text-foreground"}`}>
                  {l.tokens >= 0 ? "+" : ""}{l.tokens}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-start gap-3 rounded-2xl border border-border bg-accent/40 p-5 text-sm text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#0B5D3B] dark:text-[#25D366]" />
        ThinkInk helps you understand and improve your own work. We never write assignments, and your materials are only ever used in your own requests.
      </div>
    </div>
  );
}
